import React, { useContext } from 'react';
import { makeStyles } from 'tss-react/mui';
import {
  IRapportUtdelning,
  IGrundurval,
} from '../../../../Queries/AnalysutdelningQuery/AnalysutdelningQuery';
import NodeUrvalQuery from '../../../../../../../../../Components/Urval/Components/SavedUrval/Queries/NodeUrvalQuery/NodeUrvalQuery';
import EDStandardSelect from '../../../../../../../../../BaseComponents/Selects/StandardSelect/EDStandardSelect';
import { EMPTY_URVAL } from '../../../../Mutations/ChangeUtdelningar/ChangeUtdelningar';
import { ISelectChoice } from '../../../../../../../../../BaseComponents/Selects/SelectCommons';
import InfoText from '../../../../../../../../../Components/InfoText/InfoText';
import UrvalGQIDQuery from '../../../../../../../../../Components/Urval/Queries/UrvalGQIDQuery/UrvalGQIDQuery';
import { convertUrval } from '../../../../../../../../../Components/Urval/Components/SavedUrval/Utils/convertSavedUrval';
import { ModelContext } from '../../../../../../../../../Model/ModelContext/ModelContext';
import { KodplanContext } from '../../../../../../../../../Components/KodplanManager/Context/KodplanContext';
import {
  urvalFromKodplan,
  getEmptyUrval,
} from '../../../../../../../../../Components/Urval/Utils/urvalUtils';
import KodbehorighetInfo from '../../../../../../../../../Components/KodbehorighetInfo/KodbehorighetInfo';

interface IProps {
  analys: IRapportUtdelning;
  onGrundUrvalChange: (grundUrvalId: string) => void;
  urvalOptions: IGrundurval[];
}

const useStyles = makeStyles()({
  select: {
    minWidth: '228px',
  },
  formControlRoot: {
    marginTop: 0,
    marginBottom: 0,
  },
});

const sectionNames = {
  standard: 'Standard grundurval',
  egna: 'Egna grundurval',
};

/**
 * Komponent som fasciliterar visning av grundurvalet
 */
const AnalysUtdelningGrundurvalDisplay: React.FunctionComponent<
  React.PropsWithChildren<IProps>
> = props => {
  const { classes } = useStyles(undefined, {
    props: props as any,
  });
  const modelContext = useContext(ModelContext);
  const kodplanContext = useContext(KodplanContext);

  // Menyalternativ som inte är riktiga grundurval
  const addedItems: Array<ISelectChoice<string>> = [];
  if (!props.analys.isStandard || props.analys.grundUrval != null) {
    addedItems.push({
      icon: 'edit' as 'edit',
      header: props.analys.isStandard
        ? 'Visa grundurval'
        : 'Hantera grundurval',
      value: 'ADD_NEW',
      section: undefined,
    });
  }
  if (!props.analys.isStandard || props.analys.grundUrval === null) {
    addedItems.push({
      header: EMPTY_URVAL.name,
      value: '',
      section: undefined,
    });
  }

  const { isStandard, grundUrval } = props.analys;

  return (
    <>
      {!props.analys.isStandard && (
        <EDStandardSelect<string>
          rootClass={classes.select}
          formControlRoot={classes.formControlRoot}
          value={
            props.analys.grundUrval ? props.analys.grundUrval.id || '' : ''
          }
          renderValue={_ =>
            props.analys.grundUrval ? props.analys.grundUrval.name : ''
          }
          onChange={props.onGrundUrvalChange}
          sections={[sectionNames.standard, sectionNames.egna]}
          items={[
            ...addedItems,
            ...props.urvalOptions
              .filter(
                u =>
                  !props.analys.isStandard ||
                  (props.analys.grundUrval &&
                    props.analys.grundUrval.id === u.id)
              )
              .map(u => ({
                header: u.name,
                value: u.id || '',
                section: u.standardGrundurval
                  ? sectionNames.standard
                  : sectionNames.egna,
              })),
          ]}
          fullWidth
        />
      )}
      {isStandard && grundUrval && (
        <UrvalGQIDQuery
          sysUrvalId={
            props.analys.grundUrval
              ? props.analys.grundUrval.id || undefined
              : undefined
          }
        >
          {({ error, loading, data }) => {
            return (
              <NodeUrvalQuery
                id={
                  data && data.node && data.node.__typename === 'TEDSYSUrval'
                    ? data.node.SelectionGQID
                    : undefined
                }
              >
                {({
                  loading: nodeUrvalLoading,
                  error: nodeUrvalError,
                  urval: nodeUrval,
                }) => {
                  if (error || loading || nodeUrvalError || nodeUrvalLoading) {
                    return null;
                  }

                  const urval =
                    props.analys.grundUrval && nodeUrval
                      ? convertUrval(
                          nodeUrval,
                          'EKKonto',
                          modelContext.avenyCollectionMap
                        )
                      : kodplanContext.activeKodplan
                      ? urvalFromKodplan(
                          kodplanContext.activeKodplan,
                          modelContext.avenyCollectionMap
                        )
                      : getEmptyUrval();
                  return (
                    <InfoText
                      dialogTitle={grundUrval.name}
                      buttonSize="medium"
                      text={grundUrval.name}
                      tooltipText="Visa urval för kodbehörigheten"
                    >
                      {grundUrval && (
                        <KodbehorighetInfo kodbehorigheturval={[urval]} />
                      )}
                    </InfoText>
                  );
                }}
              </NodeUrvalQuery>
            );
          }}
        </UrvalGQIDQuery>
      )}
    </>
  );
};

export default AnalysUtdelningGrundurvalDisplay;
